import { buttonClasses, Components, Theme, touchRippleClasses } from '@mui/material';
import { keyframes } from '@mui/material/styles';

import { paletteGreen, paletteGrey, paletteRed } from './Palette';

const rippleEnterOpaque = keyframes`
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

type MuiButtonTheme = Components<Theme>['MuiButton'];

/* View of all the button styles can be found here:
    https://prismacode.visualstudio.com/prismacloud/_wiki/wikis/prismacloud.wiki/2611/MUI-theme# */
export const buttonTheme: MuiButtonTheme = {
  defaultProps: {
    variant: 'contained',
    disableRipple: false,
  },
  styleOverrides: {
    startIcon: {
      marginTop: '-1px',
      marginRight: '4px',
    },
    endIcon: {
      marginTop: '-1px',
      marginLeft: '4px',
    },
    root: ({ ownerState, theme }) => ({
      height: 32,
      minWidth: 96,
      transition: theme.transitions.create('all', { duration: theme.transitions.duration.shortest }),
      fontFamily: 'Roboto',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '16px',
      borderRadius: 4,
      textTransform: 'none',
      boxShadow: 'none',
      paddingTop: '7px',
      paddingLeft: '20px',
      paddingRight: '20px',
      zIndex: 0, // creates stacking context so ripple z-index: -1 stays below text
      [`& .${touchRippleClasses.root}`]: {
        zIndex: -1,
      },
      [`& .${touchRippleClasses.rippleVisible}`]: {
        opacity: 1,
        animationName: `${rippleEnterOpaque}`,
      },

      // Primary button (contained, color: primary)
      ...(ownerState.variant === 'contained' &&
        ownerState.color === 'primary' && {
          backgroundColor: theme.palette.primary.main,
          color: '#fff',
          border: 'none',
          ['&:hover']: {
            backgroundColor: theme.palette.primary.dark,
            color: '#fff',
            boxShadow: 'none',
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: theme.palette.action.disabled,
            color: '#fff',
          },
          [`& .${touchRippleClasses.child}`]: {
            backgroundColor: paletteGreen[600],
          },
        }),

      // Secondary button (outlined, color: primary)
      ...(ownerState.variant === 'outlined' &&
        ownerState.color === 'primary' && {
          backgroundColor: '#fff',
          color: theme.palette.primary.dark,
          border: `1px solid ${theme.palette.primary.dark}`,
          ['&:hover']: {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.dark,
            border: `1px solid ${theme.palette.primary.dark}`,
            boxShadow: 'none',
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: '#fff',
            color: paletteGrey[300],
            border: `1px solid ${theme.palette.action.disabled}`,
          },
          [`& .${touchRippleClasses.child}`]: {
            backgroundColor: paletteGreen[100],
          },
        }),

      // Tertiary button (outlined, color: secondary)
      ...(ownerState.variant === 'outlined' &&
        ownerState.color === 'secondary' && {
          backgroundColor: '#fff',
          color: paletteGrey[600],
          border: `1px solid ${paletteGrey[500]}`,
          ['&:hover']: {
            backgroundColor: paletteGrey[50],
            color: paletteGrey[600],
            border: `1px solid ${paletteGrey[500]}`,
            boxShadow: 'none',
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: '#fff',
            color: paletteGrey[300],
            border: `1px solid ${theme.palette.action.disabled}`,
          },
          [`& .${touchRippleClasses.child}`]: {
            backgroundColor: paletteGrey[100],
          },
        }),

      // Destructive primary button (contained, color: error)
      ...(ownerState.variant === 'contained' &&
        ownerState.color === 'error' && {
          backgroundColor: paletteRed[400],
          color: '#fff',
          border: 'none',
          ['&:hover']: {
            backgroundColor: theme.palette.error.dark,
            color: '#fff',
            boxShadow: 'none',
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: theme.palette.action.disabled,
            color: '#fff',
          },
          [`& .${touchRippleClasses.child}`]: {
            backgroundColor: paletteRed[100],
          },
        }),

      // Destructive secondary button (outlined, color: error)
      ...(ownerState.variant === 'outlined' &&
        ownerState.color === 'error' && {
          backgroundColor: '#fff',
          color: paletteGrey[600],
          border: `1px solid ${paletteGrey[500]}`,
          ['&:hover']: {
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.main,
            border: `1px solid ${theme.palette.error.main}`,
            boxShadow: 'none',
          },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: '#fff',
            color: paletteGrey[300],
            border: `1px solid ${theme.palette.action.disabled}`,
          },
          [`& .${touchRippleClasses.child}`]: {
            backgroundColor: paletteRed[100],
          },
        }),
    }),
  },
};
